import { AxiosError } from 'axios';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, getErrorMessage } from '@/utils';
import { API_ENDPOINTS } from '@/constants';
import { useSnackbar } from '@/context/SnackbarContext';

import type { InsightFeedbackRating, InsightFeedbackReason } from '@/types/insights';

interface SubmitFeedbackArgs {
    questionId: number;
    rating: InsightFeedbackRating | null;
    reason?: InsightFeedbackReason | null;
    comment?: string | null;
}

/**
 * Saves or clears a thumbs up/down on one insight.
 *
 * A null rating clears the existing one. The call insights query is invalidated on success,
 * so the icons re-render from the server rather than from local state.
 */
const useInsightFeedback = (callId: number) => {
    const queryClient = useQueryClient();
    const { showSnackbar } = useSnackbar();

    return useMutation({
        mutationFn: async ({ questionId, rating, reason, comment }: SubmitFeedbackArgs): Promise<void> => {
            if (rating === null) {
                await api.delete(API_ENDPOINTS.CALL_INSIGHT_FEEDBACK_BY_QUESTION(callId, questionId));
                return;
            }
            await api.post(API_ENDPOINTS.CALL_INSIGHT_FEEDBACK(callId), {
                questionId,
                rating,
                reason: reason ?? null,
                comment: comment ?? null,
            });
        },

        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ['api', 'call', callId, 'insights'] });
        },

        onError: (error: AxiosError) => {
            showSnackbar(getErrorMessage(error) ?? 'Failed to save feedback', 'error');
        },
    });
};

export default useInsightFeedback;
